import { ComponentType, useContext, useMemo } from "react";
import { createRoot } from "react-dom/client";
import { AsyncJobsProvider } from "@kaltura/mediaspace-shared-contexts/AsyncJobsProvider";
import { EmbeddedCncWidgetContextProvider } from "@kaltura/mediaspace-shared-contexts/EmbeddedCncWidgetContext";
import { KmsUnisphereProvider } from "@kaltura/mediaspace-shared-contexts/KmsUnisphereProvider";
import { Config } from "@kaltura/mediaspace-shared-data-kms-config/Config";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config/ConfigContext";
import { ConfigSidebarButton } from "@kaltura/mediaspace-shared-data-kms-config/ConfigSidebarButton";
import { SidebarButtonProps, SidebarLayout } from "@kaltura/ds-react-layouts";
import { KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { styled } from "@mui/material";
import { AsyncJobsChecker } from "./async-jobs";
import { getComponentFromPath } from "@kaltura/mediaspace-shared-utils";
import { UnisphereNotificationsProvider } from "@unisphere/notifications-runtime-react";
import { TopLevelComponentWrappers } from "./TopLevelComponentWrappers";

export interface DsLayoutProps<T extends object> extends Omit<KmsTypeComponent<T>, "name"> {
    context: Config;
}

// make the main region 100% height
const StyledMainRegionWrapper = styled("div")({
    display: "flex",
    flexDirection: "column",
    minHeight: "inherit",
});

const StyledMainContentRegionWrapper = styled("div")({
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
});

/**
 * Render a component in a full-react layout (the whole page is rendered here, including the header and the footer).
 * All layout-specific components are supposed to come from the context.
 *
 * Regions illustration: https://i.gyazo.com/b47c2860d2404292c1ac0585f78ba5e6.png
 */
export const DsLayout = <T extends object>({ context, ...mainComponent }: DsLayoutProps<T>) => {
    const { dsLayout } = context;

    const renderRegion = (items: Omit<KmsTypeComponent, "name">[] = [], keyPrefix = "") =>
        items
            .map((item, index) => ({ ...item, index }))
            // sort by order, preserve original order when the order is the same
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.index - b.index)
            .map(({ component, props, index }) => {
                const Component = getComponentFromPath(component);
                return <Component key={keyPrefix + index} {...props} />;
            });

    const configContext = useContext(ConfigContext);
    const mainAreaAttributes = !configContext.application?.isKAF
        ? {
              id: "contentWrap",
              role: "main",
              tabIndex: -1,
          }
        : {};
    const renderMainRegion = () => {
        return (
            <StyledMainRegionWrapper className={"kms-ds-main-region"}>
                {renderRegion(dsLayout?.components.header, "header")}
                <StyledMainContentRegionWrapper {...mainAreaAttributes}>
                    {renderRegion([mainComponent, ...(dsLayout?.components.main ?? [])], "main")}
                </StyledMainContentRegionWrapper>
                {renderRegion(dsLayout?.components.footer, "footer")}
            </StyledMainRegionWrapper>
        );
    };

    const defaultButtonId = dsLayout?.defaultSidebarTopButtonId;

    const formattedButtons = useMemo(
        () =>
            dsLayout?.sidebarTopButtons?.map(
                <ButtonProps, ContentsProps extends object>({
                    buttonDescriptor: { contentsComponent, ...button },
                    contentsProps,
                }: ConfigSidebarButton<ButtonProps, ContentsProps>): SidebarButtonProps<ButtonProps> => {
                    const Contents = getComponentFromPath(contentsComponent);
                    return {
                        ...button,
                        contents: <Contents {...contentsProps} />,
                    };
                }
            ),
        [dsLayout?.sidebarTopButtons]
    );

    const showButtonsRail = dsLayout?.sidebarTopButtons?.some(({ showInRail }) => showInRail !== false) ?? false;

    return (
        <TopLevelComponentWrappers context={context}>
            <KmsUnisphereProvider>
                <EmbeddedCncWidgetContextProvider>
                    <AsyncJobsProvider initValue={context.application.shouldCheckAsyncJobsStatus}>
                        <UnisphereNotificationsProvider>
                            <AsyncJobsChecker />
                            <SidebarLayout
                                mainRegion={renderMainRegion()}
                                floatedRegion={renderRegion(dsLayout?.components.floated)}
                                sidebarButtons={formattedButtons}
                                defaultOpen={!!defaultButtonId}
                                defaultButtonId={defaultButtonId}
                                sidebarBottomButtonsRegion={renderRegion(dsLayout?.components.sidebarBottomButtons)}
                                showButtonsRail={showButtonsRail}
                            />
                        </UnisphereNotificationsProvider>
                    </AsyncJobsProvider>
                </EmbeddedCncWidgetContextProvider>
            </KmsUnisphereProvider>
        </TopLevelComponentWrappers>
    );
};

/**
 * Render component in full-react layout.
 * This function could be used directly on the PHP side, e.g.
 * ```
 * <div id="some-id"></div>
 * <script>
 *     MEDIASPACE.DsLayout.renderPageComponent(
 *         'some-id',
 *         MEDIASPACE.AppName.ComponentReference,
 *         <?php echo $this->jsCodeEcho($this->props + ['context' => $configContext]); ?>
 *     );
 * </script>
 * ```
 */
export const renderPageComponent = <T extends object>(
    id: string,
    component: ComponentType<T>,
    props: T & { context: Config },
    order?: number
) => {
    const root = createRoot(document.getElementById(id)!);
    root.render(<DsLayout context={props.context} component={component} props={props as T} order={order} />);
};

/**
 * Return a render function for rendering component in full-react layout.
 * This helper could be used in a React app to prepare a page render function for the client side.
 * The render function takes element ID as the first argument, and component's props with a context object as the second argument,
 * so it would be used like the following on the PHP side:
 * ```
 * <div id="some-id"></div>
 * <script>
 *     MEDIASPACE.AppName.render('some-id', <?php echo $this->jsCodeEcho($this->props + ['context' => $configContext]); ?>);
 * </script>
 * ```
 */
export const getDsLayoutComponentRenderer =
    <T extends object>(component: ComponentType<T>, order?: number) =>
    (id: string, props: T & { context: Config }) =>
        renderPageComponent(id, component, props, order);
